import { RequestInformationContent } from '../pages/RequestInformationPage'

// Landing pages for a single area of study, keyed by "<area-slug>-<version>"
// and served at /areas-of-study/<area-slug>/<version>.
//
// Each entry pins the area (so the form asks for degree level + program
// within that area instead of the area itself) and supplies its own ad copy,
// so multiple versions of the same area can be tested side by side.
export interface AreaStudyLanding {
  // Area of study id from data/filterOptions.ts — drives the program list
  // and the lead payload's areaStudy value.
  areaId: number
  // Human-readable area name used in the copy.
  areaName: string
  content: RequestInformationContent
}

export const areaStudyLandings: Record<string, AreaStudyLanding> = {
  'psychology-v1': {
    areaId: 3,
    areaName: 'Psychology & Counseling',
    content: {
      metaTitle: 'Online Psychology Degrees | FindOnlineDegrees',
      metaDescription:
        'Compare accredited online psychology programs — counseling, clinical, forensic, and more. Get matched in about 60 seconds.',
      badge: 'Accredited Psychology Programs',
      headline: 'Study What Makes',
      headlineAccent: 'People Tick',
      subhead:
        'From counseling to forensic psychology, compare accredited online programs built around working adults. Tell us your goal and we’ll match you with schools now enrolling.',
      heroBackgroundImage:
        'https://cdn.magicpatterns.com/uploads/hsXWZMsgnxfLTQtE9SFKeS/3cef6c5d-7d6d-4502-9ea9-740f6e17e995.png',
      heroOverlay: 'light',
      features: [
        'Counseling, clinical, forensic, and applied tracks',
        'Associate through doctoral programs',
        'Flexible online coursework',
        'Financial aid guidance',
      ],
      formHeading: 'Find Your Psychology Program',
      formSubhead: 'Choose your degree level and focus',
      supportNote: {
        heading: 'Is an online psychology degree right for you?',
        body: 'Psychology is one of the most flexible fields you can study — the same foundation opens doors in counseling, social services, human resources, healthcare, research, and criminal justice. Here’s what to weigh before you choose a program.',
        points: [
          {
            icon: 'degree',
            title: 'Match the degree to the role',
            body: 'A bachelor’s prepares you for case management, HR, and behavioral health support roles. Licensed counseling and therapy generally require a master’s, and research or clinical practice a doctorate.',
          },
          {
            icon: 'compass',
            title: 'Pick a focus that fits your goal',
            body: 'Clinical and counseling tracks lead toward direct client work. Forensic, organizational, and developmental tracks point toward legal, workplace, and education settings.',
          },
          {
            icon: 'license',
            title: 'Plan for licensure early',
            body: 'Licensing rules are set state by state and usually include supervised practicum hours. Ask each school how their online program handles placements where you live.',
          },
        ],
        footnote:
          'Program availability, admissions requirements, and licensure pathways vary by school and state. Confirm details with each school before enrolling.',
      },
    },
  },
  'health-care-v1': {
    areaId: 5,
    areaName: 'Healthcare & Medical Assisting',
    content: {
      metaTitle: 'Online Healthcare Programs | FindOnlineDegrees',
      metaDescription:
        'Compare accredited healthcare and medical assisting programs — medical assisting, billing and coding, health administration, and more.',
      badge: 'Accredited Healthcare Programs',
      headline: 'Start a Career That',
      headlineAccent: 'Takes Care of People',
      subhead:
        'Medical assisting, billing and coding, health information, and healthcare administration — compare accredited programs designed for working adults and get matched with schools now enrolling.',
      features: [
        'Medical assisting and patient care tracks',
        'Billing, coding, and health information',
        'Certificate through master’s programs',
        'Financial aid guidance',
      ],
      formHeading: 'Find Your Healthcare Program',
      formSubhead: 'Choose your degree level and focus',
      heroBackgroundImage:
        'https://cdn.magicpatterns.com/uploads/75qUZDpuXF5eXLrCNvNWFh/0c15f03b-dc4f-4501-8625-8837846189c7.png',
      heroOverlay: 'light',
      supportNote: {
        heading: 'Choosing the right healthcare program',
        body: 'Healthcare hires at every level — some roles start with a short certificate, others need a degree. The fastest path depends on whether you want to work directly with patients, behind the scenes with records and billing, or on the management side.',
        points: [
          {
            icon: 'clock',
            title: 'Know how long it takes',
            body: 'Certificates and diplomas can often be finished in under a year, associate degrees in about two, and bachelor’s or master’s programs when you’re aiming at administration or leadership.',
          },
          {
            icon: 'briefcase',
            title: 'Patient-facing or behind the scenes',
            body: 'Medical assisting and patient care put you in the exam room. Billing, coding, and health information keep you working with records, insurers, and data.',
          },
          {
            icon: 'license',
            title: 'Check certification and clinicals',
            body: 'Many roles expect an industry certification, and hands-on programs include externships. Ask each school how clinical hours are arranged near you.',
          },
        ],
        footnote:
          'Program availability, certification requirements, and clinical placement vary by school and state. Confirm details with each school before enrolling.',
      },
    },
  },
  'business-v1': {
    areaId: 1,
    areaName: 'Business & Finance',
    content: {
      metaTitle: 'Online Business Degrees | FindOnlineDegrees',
      metaDescription:
        'Compare accredited online business programs — management, accounting, finance, marketing, and MBA options.',
      badge: 'Accredited Business Programs',
      headline: 'Build the Career',
      headlineAccent: 'You Want to Run',
      subhead:
        'Management, accounting, finance, marketing, and MBA options from accredited online schools. Tell us your goal and we’ll match you with programs now enrolling.',
      features: [
        'MBA and specialized master’s options',
        'Accounting, finance, marketing, and management',
        'Study around a full-time job',
        'Financial aid guidance',
      ],
      formHeading: 'Find Your Business Program',
      formSubhead: 'Choose your degree level and focus',
    },
  },
  'nursing-v1': {
    areaId: 10,
    areaName: 'Nursing',
    content: {
      metaTitle: 'Online Nursing Degrees | FindOnlineDegrees',
      metaDescription:
        'Compare accredited online nursing programs — RN to BSN, MSN, nurse practitioner, and more.',
      badge: 'Accredited Nursing Programs',
      headline: 'Advance Your',
      headlineAccent: 'Nursing Career',
      subhead:
        'RN to BSN, MSN, and nurse practitioner tracks from accredited online schools built for working nurses. Tell us your goal and we’ll match you with programs now enrolling.',
      features: [
        'RN to BSN and bridge options',
        'MSN and nurse practitioner tracks',
        'Coursework online, clinicals near you',
        'Financial aid guidance',
      ],
      formHeading: 'Find Your Nursing Program',
      formSubhead: 'Choose your degree level and focus',
    },
  },
}

export function getAreaStudyLanding(
  areaSlug: string | undefined,
  version: string | undefined,
): AreaStudyLanding | null {
  if (!areaSlug || !version) return null
  const key = `${areaSlug}-${version}`.toLowerCase()
  return areaStudyLandings[key] || null
}
